import { DataTable } from "@shopify/polaris";
import React, { useCallback } from "react";
import styled from "styled-components";

import { formatTime, handleError, objectInvalid } from "../../helper";
import ElementScrollable from "../shared/ElementScrollable";
import EmptyStatePolaris from "../shared/EmptyStatePolaris";
import PaginationPolaris from "../shared/PaginationPolaris";
import SelectRow from "../shared/SelectRow";
import SkeletonPagePolaris from "../shared/SkeletonPagePolaris";
import AmazonIdeaTemplateAction from "./amazon-idea-template-action";
import { useAmazonIdeaTemplateCtx } from "./context";
import AmazonIdeaTemplateBulkAction from "./amazon-idea-template-bulk-action";

function AmazonIdeaTemplateTable() {
    const {
        loading,
        error,
        amazonIdeaTemplates,
        setFilter,
        filter,
        total,
        refetch,
    } = useAmazonIdeaTemplateCtx();

    const handlePaginationChange = useCallback(
        (offset, limit) => {
            setFilter({ offset, limit });
        },
        [setFilter],
    );

    let { limit, offset } = filter;
    const totalPage = Math.ceil(total / limit);
    const page = offset / limit + 1;
    const aggregation = {
        page,
        totalPage,
        offset,
        limit,
        onChange: handlePaginationChange,
        total,
    };

    return (
        <>
            <Wrapper>
                {loading ? (
                    <SkeletonPagePolaris />
                ) : error ? (
                    <div>Error: {handleError(error?.toString())}</div>
                ) : amazonIdeaTemplates?.length > 0 ? (
                    <>
                        <SelectRow
                            data={amazonIdeaTemplates}
                            refetch={refetch}
                            actionMarkup={<AmazonIdeaTemplateBulkAction />}
                        >
                            {({ checkAllMarkup, checkItemMarkup }) => (
                                <ElementScrollable fixedColumn>
                                    <DataTable
                                        rows={
                                            getRows(
                                                amazonIdeaTemplates,
                                                checkItemMarkup,
                                            ) || []
                                        }
                                        columnContentTypes={[
                                            ...Array.from({ length: 13 }).fill(
                                                "text",
                                            ),
                                            "numeric",
                                        ]}
                                        headings={[
                                            checkAllMarkup,
                                            "ID",
                                            "Title",
                                            "Author",
                                            "Created At",
                                            <div className="actions-wrap">
                                                Actions
                                            </div>,
                                        ]}
                                        hideScrollIndicator
                                        verticalAlign="middle"
                                    />
                                </ElementScrollable>
                            )}
                        </SelectRow>
                        <PaginationContainer>
                            <PaginationPolaris
                                aggregation={aggregation}
                                showTotal
                            />
                        </PaginationContainer>
                    </>
                ) : (
                    <EmptyStatePolaris />
                )}
            </Wrapper>
        </>
    );
}

function getRows(nodes, checkItemMarkup) {
    const res = [];

    for (const node of nodes) {
        if (objectInvalid(node)) continue;

        const { id, title, createdAt } = node;

        const item = [
            checkItemMarkup ? checkItemMarkup(id) : null,
            <div className="id item">
                <span>{id}</span>
            </div>,
            <div className="title item">
                <span>{title}</span>
            </div>,
            <div className="author item">
                <span>{getUser(node.author)}</span>
            </div>,
            <div className="created-at item">
                <span>{formatTime(createdAt)}</span>
            </div>,
            <div className="actions-wrap item">
                <AmazonIdeaTemplateAction node={node} />
            </div>,
        ];

        res.push(item);
    }

    return res;
}

function getUser(user) {
    if (!user) return "";
    return [user.firstName, user.lastName].filter(Boolean).join(" ");
}

const Wrapper = styled.div`
    overflow-x: auto;

    .Polaris-DataTable__ScrollContainer {
        overflow-x: inherit;
    }

    .item {
        white-space: normal;
        word-break: break-word;
    }

    .actions-wrap {
        width: calc(13.5rem - 3.2rem);
        margin-left: auto;
    }

    .author,
    .id,
    .created-at {
        width: calc(18rem - 3.2rem);
    }

    .title {
        width: calc(20rem - 3.2rem);
    }
`;

const PaginationContainer = styled.div`
    padding: 1.6rem;
    overflow-x: scroll;
    background: var(--p-surface-subdued, #f4f6f8);
`;

export default AmazonIdeaTemplateTable;
